/**
 * Панель «Платье» видео-проекта (левая колонка `/project/:id`, спека §9.2): обложка, фото папки,
 * ссылка в «Наши фото», «Наполнить слоты заново» — фото по дате снова встают в `product.*` проекта
 * (повтор безвреден: строки библиотеки и привязки — UPSERT).
 */

import { RefreshCw } from "lucide-react";
import { Link } from "react-router";

import { Btn, ErrorLine, Hint, Panel } from "@/components/project/ui";
import { mediaUrl } from "@/lib/media";
import { errorText } from "@/lib/project";
import { useFill, type VideoDress } from "@/lib/video";

export function DressPanel({ pid, dress, slots = true }: { pid: number; dress: VideoDress; /** «Наполнить слоты заново» — только у проекта ИЗ ПЛАТЬЯ: проекту «из кадра» слоты не нужны (№321). */ slots?: boolean }) {
  const fill = useFill(pid);
  const cover = dress.photos[0];

  // `id === null` — платья у проекта не было вовсе (ролик из картинки без папки); `deleted` с
  // номером — папку удалили. Разные случаи: писать про удаление там, где удалять было нечего,
  // значит врать сотруднику (найдено ревью 16.10).
  if (dress.id === null) {
    return (
      <Panel title="Платье" annot="платье ролика">
        <Hint>У ролика нет платья: он сделан из картинки без папки «Наших фото».</Hint>
      </Panel>
    );
  }
  if (dress.deleted) {
    return (
      <Panel title="Платье" annot="платье ролика">
        <Hint>Папку платья удалили — готовые клипы и кадры остаются в проекте.</Hint>
      </Panel>
    );
  }

  return (
    <Panel
      title="Платье"
      aside={
        <Link to={`/photos/${dress.id}`} className="hover:text-accent-text">
          в «Наших фото» →
        </Link>
      }
      annot="платье ролика"
    >
      <div className="flex gap-3">
        <Link to={`/photos/${dress.id}`} className="flex-none">
          {cover?.thumb_path || cover?.view_path ? (
            <img
              src={mediaUrl(cover.thumb_path ?? cover.view_path)}
              alt=""
              className="h-[96px] w-[64px] rounded-[3px] border border-border object-cover"
            />
          ) : (
            <div className="grid h-[96px] w-[64px] place-items-center rounded-[3px] border border-dashed border-border-strong text-[11px] text-fg-faint">
              нет фото
            </div>
          )}
        </Link>
        <div className="min-w-0">
          <div className="truncate text-[13px] font-medium text-fg">{dress.name}</div>
          <Hint>фото: {dress.photos.length}</Hint>
          {dress.note ? <div className="mt-1 text-[12px] text-fg-muted">{dress.note}</div> : null}
        </div>
      </div>

      {dress.photos.length > 1 ? (
        <div className="mt-3 flex flex-wrap gap-1.5">
          {dress.photos.slice(1).map((p) =>
            p.thumb_path || p.view_path ? (
              <img
                key={p.id}
                src={mediaUrl(p.thumb_path ?? p.view_path)}
                alt=""
                loading="lazy"
                className="h-[48px] w-[32px] rounded-[3px] border border-border object-cover"
              />
            ) : null,
          )}
        </div>
      ) : null}

      {slots ? (
        <>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <Btn onClick={() => fill.mutate({})} disabled={fill.isPending}>
              <RefreshCw size={13} />
              Наполнить слоты заново
            </Btn>
            {fill.data ? (
              <Hint>
                в слотах проекта: {fill.data.project_slots.length} · без слота: {fill.data.loose}
              </Hint>
            ) : null}
          </div>
          <Hint className="mt-1.5">Фото по дате встают в слоты «товар» проекта (до пяти), остальные — в библиотеку проекта.</Hint>
          <ErrorLine text={fill.isError ? errorText(fill.error) : ""} className="mt-2" />
        </>
      ) : null}
    </Panel>
  );
}
